import {t} from "i18next";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import {useEffect, useRef, useState} from "react";
import {api} from "../../../services/api/api";
import {getAttributeHint, getReportDataHint, isAttributeAllowed} from "./attributeHints";

export default function ReportForm({
                                     data,
                                     onChange,
                                     onSubmit,
                                     submitted
                                   }: {
  data?: EasyTech.ReportItem,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean
}) {
  const [dataValueState, setDataValueState] = useState<any>(null);
  const [areaValueState, setAreaValueState] = useState<any>(null);
  const [allOptions, setAllOptions] = useState<any[]>([]);
  const optionsCacheRef = useRef<any[] | null>(null);

  const areaOptions = [
    {value: "HEADER_LEFT", label: t("form:report.area.header"), hint: t("form:report.area.leftColumn")},
    {value: "HEADER_RIGHT", label: t("form:report.area.header"), hint: t("form:report.area.rightColumn")},
    {value: "ADDITIONAL_INFOS", label: t("workorder:additionalInfos"), hint: t("form:report.area.tableAfterParts")},
    {value: "FOOTER", label: t("form:report.area.footer"), hint: t("form:report.area.endOfReport")}
  ];

  const resolveSelectedOption = (val: string) => ({
    value: val,
    label: val,
    hint: getReportDataHint(val),
  });

  useEffect(() => {
    if (data?.data) {
      const found = allOptions.find(o => o.value === data.data);
      if (found) {
        setDataValueState(found);
      } else {
        setDataValueState(resolveSelectedOption(data.data));
      }
    } else {
      setDataValueState(null);
    }

    if (data?.area) {
      const defaultArea = areaOptions.find(o => o.value === data.area);
      if (defaultArea) setAreaValueState(defaultArea);
    } else {
      setAreaValueState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, allOptions]);

  const fetchAllOptions = (): Promise<any[]> => {
    if (optionsCacheRef.current) {
      return Promise.resolve(optionsCacheRef.current);
    }

    const getAttributes = api.get("admin/workorders/attributes")
      .then((ret) => {
        const attributes = ret as any[];
        const currentLanguage = localStorage.getItem("i18nextLng") || "en";

        return attributes
          .filter(attr => {
            const path = typeof attr === "string" ? attr : attr.path;
            return path && isAttributeAllowed(path);
          })
          .map(attr => {
            if (typeof attr === "string") {
              return {
                value: attr,
                label: attr,
                hint: getAttributeHint(attr),
              };
            }

            let resolvedLabel = attr.label || attr.path;
            if (attr.translations && Array.isArray(attr.translations)) {
              const translation = attr.translations.find(
                (t: any) => t.language?.isoCode === currentLanguage
              );
              if (translation && translation.label) {
                resolvedLabel = translation.label;
              }
            }

            return {
              value: attr.path,
              label: resolvedLabel,
              hint: getAttributeHint(attr.path),
            };
          });
      })
      .catch(() => []);

    const getCompInfos = api.get("admin/compinfos/inputs")
      .then((ret) => {
        const compInfos = ret as EasyTech.CompInfo[];
        return compInfos.map(ci => ({
          value: ci.label || ci.id,
          label: ci.label,
          hint: t("form:report.customField")
        }));
      })
      .catch(() => []);

    const getSelects = api.get("admin/compinfos/selects")
      .then((ret) => {
        const compInfos = ret as EasyTech.CompInfo[];
        return compInfos.map(ci => ({
          value: ci.label || ci.id,
          label: ci.label,
          hint: t("form:report.customField")
        }));
      })
      .catch(() => []);

    return Promise.all([getAttributes, getCompInfos, getSelects]).then(([attrOptions, compOptions, selects]) => {
      const merged = [...attrOptions, ...compOptions, ...selects];
      optionsCacheRef.current = merged;
      setAllOptions(merged);
      return merged;
    });
  };

  const filterOptions = (options: any[], inputValue: string) => {
    const search = inputValue.trim().toLowerCase();
    if (!search) {
      return options;
    }

    return options.filter(opt =>
      [opt.label, opt.value, opt.hint]
        .filter(Boolean)
        .some(field => String(field).toLowerCase().includes(search))
    );
  };

  const loadDataOptions = (inputValue: string): Promise<any[]> => {
    return fetchAllOptions().then(options => filterOptions(options, inputValue));
  };

  const handleDataChange = (option: any) => {
    setDataValueState(option);
    onChange("data", option ? option.value : null);
  };

  const handleAreaChange = (option: any) => {
    setAreaValueState(option);
    onChange("area", option ? option.value : null);
  };

  const formatOptionLabel = ({label, hint}: any) => (
    <div className="flex items-center w-full gap-2">
      <div>{label}</div>
      {hint && <span className="text-gray-500 text-xs">{hint}</span>}
    </div>
  );

  return (
    <>
      <div className="field">
        <label>{t("settings:reports:fields:data", "Data")}</label>
        <AsyncSelect
          cacheOptions
          defaultOptions
          loadOptions={loadDataOptions}
          filterOption={() => true}
          value={dataValueState}
          onChange={handleDataChange}
          formatOptionLabel={formatOptionLabel}
          classNamePrefix="react-select"
          className={`${(submitted && !data?.data) ? "border border-red-500 rounded" : ""}`}
          placeholder={t("settings:reports:fields:dataPlaceholder", "Select the type of data")}
        />
      </div>

      <div className="field mt-4">
        <label>{t("settings:reports:fields:area", "Area")}</label>
        <Select
          options={areaOptions}
          value={areaValueState}
          onChange={handleAreaChange}
          formatOptionLabel={formatOptionLabel}
          classNamePrefix="react-select"
          className={`${(submitted && !data?.area) ? "border border-red-500 rounded" : ""}`}
          placeholder={t("settings:reports:fields:areaPlaceholder", "Select where to display")}
        />
      </div>
    </>
  );
}
